import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaCheckCircle, FaTimesCircle, FaQuestionCircle, FaTimes, FaUsers, FaClock, FaUserCircle } from 'react-icons/fa';
import { rsvpApi } from '../api/rsvp';
import styles from './RSVPDetailsModal.module.css';

const RSVPDetailsModal = ({ eventId, event, isOpen, onClose, initialTab = 'all' }) => {
  const [attendees, setAttendees] = useState([]);
  const [summary, setSummary] = useState({ total: 0, going: 0, notGoing: 0, tbd: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [canViewAttendees, setCanViewAttendees] = useState(true);

  // Fetch RSVP details when modal opens
  useEffect(() => {
    if (isOpen && eventId) {
      fetchRSVPDetails();
    }
  }, [isOpen, eventId]);

  const fetchRSVPDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await rsvpApi.getEventRSVPs(eventId, true);
      setAttendees(response.rsvps || []);
      setSummary(response.summary || { total: 0, going: 0, notGoing: 0, tbd: 0 });
      setCanViewAttendees(response.canViewAttendees === true);
    } catch (err) {
      setError('Failed to load RSVP details');
      console.error('Error fetching RSVP details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter attendees based on active tab
  const getFilteredAttendees = () => {
    if (activeTab === 'all') return attendees;
    return attendees.filter(attendee => attendee.response === activeTab);
  };

  // Get response icon
  const getResponseIcon = (response) => {
    switch (response) {
      case 'Going':
        return <FaCheckCircle className={styles.goingIcon} />;
      case 'Not Going':
        return <FaTimesCircle className={styles.notGoingIcon} />;
      case 'TBD':
        return <FaQuestionCircle className={styles.tbdIcon} />;
      default:
        return null;
    }
  };

  // Format response date
  const formatResponseDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get tab count
  const getTabCount = (tabType) => {
    switch (tabType) {
      case 'all':
        return summary.total;
      case 'Going':
        return summary.going;
      case 'Not Going':
        return summary.notGoing;
      case 'TBD':
        return summary.tbd;
      default:
        return 0;
    }
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <FaUsers />
            </div>
            <div className={styles.headerInfo}>
              <h2 className={styles.modalTitle}>Event RSVPs</h2>
              <p className={styles.eventTitle}>{event?.title}</p>
            </div>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close modal">
            <FaTimes />
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {/* Summary Stats */}
          <div className={styles.summaryStats}>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{summary.total}</div>
              <div className={styles.statLabel}>Total RSVPs</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{summary.going}</div>
              <div className={styles.statLabel}>Going</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{summary.tbd}</div>
              <div className={styles.statLabel}>Maybe</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{summary.notGoing}</div>
              <div className={styles.statLabel}>Not Going</div>
            </div>
          </div>

          {error && (
            <div className={styles.errorMessage}>
              <p>{error}</p>
              <button onClick={fetchRSVPDetails} className={styles.retryButton}>
                Retry
              </button>
            </div>
          )}

          {!canViewAttendees ? (
            <div className={styles.noPermission}>
              <FaUserCircle className={styles.noPermissionIcon} />
              <h3>Attendee List Not Available</h3>
              <p>You don't have permission to view the attendee list for this event.</p>
            </div>
          ) : (
            <>
              {/* Filter Tabs */}
              <div className={styles.filterTabs}>
                {[
                  { key: 'all', label: 'All', icon: FaUsers },
                  { key: 'Going', label: 'Going', icon: FaCheckCircle },
                  { key: 'TBD', label: 'Maybe', icon: FaQuestionCircle },
                  { key: 'Not Going', label: 'Not Going', icon: FaTimesCircle }
                ].map(tab => {
                  const count = getTabCount(tab.key);
                  return (
                    <button
                      key={tab.key}
                      className={`${styles.filterTab} ${activeTab === tab.key ? styles.activeTab : ''}`}
                      onClick={() => setActiveTab(tab.key)}
                      disabled={count === 0}
                    >
                      <tab.icon className={styles.tabIcon} />
                      <span className={styles.tabLabel}>{tab.label}</span>
                      <span className={styles.tabCount}>({count})</span>
                    </button>
                  );
                })}
              </div>

              {/* Attendee List */}
              <div className={styles.attendeeList}>
                {loading ? (
                  <div className={styles.loadingState}>
                    <div className={styles.spinner}></div>
                    <p>Loading RSVPs...</p>
                  </div>
                ) : getFilteredAttendees().length === 0 ? (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      {activeTab === 'all' ? <FaUsers /> : getResponseIcon(activeTab)}
                    </div>
                    <h3>No {activeTab === 'all' ? 'RSVPs' : `"${activeTab}"`} Yet</h3>
                    <p>
                      {activeTab === 'all' 
                        ? 'No one has RSVP\'d to this event yet.' 
                        : `No one has selected "${activeTab}" for this event.`}
                    </p>
                  </div>
                ) : (
                  <div className={styles.attendeeGrid}>
                    {getFilteredAttendees().map(attendee => (
                      <div key={attendee._id} className={styles.attendeeCard}>
                        <div className={styles.attendeeHeader}>
                          <div className={styles.attendeeAvatar}>
                            {(attendee.user?.avatar || attendee.user?.avatarUrl) ? (
                              <img 
                                src={attendee.user.avatar || attendee.user.avatarUrl} 
                                alt={attendee.user?.name || 'User'}
                                className={styles.avatarImage}
                              />
                            ) : (
                              <FaUserCircle className={styles.avatarPlaceholder} />
                            )}
                          </div>
                          <div className={styles.attendeeInfo}>
                            <h4 className={styles.attendeeName}>{attendee.user?.name || 'Unknown User'}</h4>
                            <p className={styles.attendeeEmail}>{attendee.user?.email}</p>
                          </div>
                          <div className={styles.responseIcon}>
                            {getResponseIcon(attendee.response)}
                          </div>
                        </div>
                        
                        <div className={styles.attendeeDetails}>
                          <div className={styles.responseStatus}>
                            <span className={styles.responseLabel}>Response:</span>
                            <span className={`${styles.responseValue} ${styles[attendee.response.toLowerCase().replace(' ', '')]}`}>
                              {attendee.response}
                            </span>
                          </div>
                          
                          <div className={styles.responseTime}>
                            <FaClock className={styles.timeIcon} />
                            <span>RSVP'd {formatResponseDate(attendee.responseDate)}</span>
                          </div>
                          
                          {attendee.note && (
                            <div className={styles.attendeeNote}>
                              <span className={styles.noteLabel}>Note:</span>
                              <p className={styles.noteText}>"{attendee.note}"</p>
                            </div>
                          )}
                          
                          {attendee.lastModified && attendee.lastModified !== attendee.responseDate && (
                            <div className={styles.lastModified}>
                              <span className={styles.modifiedLabel}>Last updated:</span>
                              <span className={styles.modifiedTime}>
                                {formatResponseDate(attendee.lastModified)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <div className={styles.footerInfo}>
            <span className={styles.footerText}>
              {summary.total > 0 && (
                <>
                  {summary.total} total RSVP{summary.total !== 1 ? 's' : ''} • 
                  Last updated: {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </>
              )}
            </span>
          </div>
          <button className={styles.closeFooterButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RSVPDetailsModal;
